import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { fmtUGX, fmtNum, shortDateTime } from "@/lib/format";
import { useState, useMemo } from "react";
import { Plus, Store, ShoppingCart, CheckCircle2, Package, Wallet, ClipboardList, Banknote, Megaphone, ShieldCheck, Truck, AlertTriangle, Clock, ChefHat, Crown, Building2, ShieldAlert, Users, Boxes, ChevronsLeft, ChevronsRight, Sparkles, Search, Database } from "lucide-react";
import { toast } from "sonner";
import { ShiftsPanel, KitchenPanel, DeliveriesPanel, ComplianceDocsPanel } from "./merchant-operations";
import { ExecutivePanel, InventoryIntelligencePanel, BulkOrdersPanel, RefundsPanel, CRMPanel, FinancePanel } from "./merchant-business-os";
import { MerchantSettlements } from "./merchant-settlements";
import { useEffectiveBrand } from "@/lib/tenant-branding";
import { cn } from "@/lib/utils";
import { MerchantOnboardingWizard } from "@/features/merchant-onboarding-wizard";
import { POSCheckout } from "@/features/merchant-pos";

type Merchant = { id: string; name: string; category: string; fee_rate_bps: number; school_id: string };
type StockItem = { id: string; merchant_id: string; name: string; price_ugx: number; quantity: number };

export function MerchantDashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: merchant } = useQuery({
    queryKey: ["my-merchant"],
    queryFn: async () => {
      const { data } = await supabase.from("merchants").select("*").eq("owner_id", user!.id).maybeSingle();
      return data as Merchant | null;
    },
  });

  if (!merchant) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-4xl">Merchant Console</h1>
          <p className="text-muted-foreground">Complete onboarding to open your till. Your progress is saved after every step.</p>
        </div>
        <MerchantOnboardingWizard ownerId={user!.id} onDone={() => qc.invalidateQueries()} />
      </div>
    );
  }
  return <ConsoleShell merchant={merchant} />;
}

function CreateMerchantCard({ onDone }: { onDone: () => void }) {
  const { user } = useAuth();
  const [name, setName] = useState("Sunset Canteen");
  const [category, setCategory] = useState<"canteen" | "bookshop" | "uniform" | "bus" | "clinic" | "hostel" | "events" | "general">("canteen");
  const [schoolId, setSchoolId] = useState("");

  const { data: schools = [] } = useQuery({
    queryKey: ["schools-all"],
    queryFn: async () => (await (supabase.from as any)("schools_public").select("id,name")).data ?? [],
  });

  const submit = useMutation({
    mutationFn: async (withSeed: boolean) => {
      const sid = schoolId || schools[0]?.id;
      if (!name || !sid) throw new Error("Fill all fields");
      const { data: m, error } = await supabase.from("merchants").insert({
        owner_id: user!.id, name, category, school_id: sid, fee_rate_bps: category === "bus" ? 100 : 200,
      } as never).select("*").single();
      if (error) throw error;
      if (withSeed && m) {
        const items = category === "canteen" ? [
          { name: "Chapati", price_ugx: 1000, quantity: 80 },
          { name: "Meat pie", price_ugx: 2500, quantity: 40 },
          { name: "Mandazi", price_ugx: 800, quantity: 120 },
          { name: "Fresh juice 300ml", price_ugx: 2000, quantity: 60 },
          { name: "Rolex (egg roll)", price_ugx: 3500, quantity: 30 },
          { name: "Posho & beans plate", price_ugx: 4500, quantity: 50 },
          { name: "Pilau & chicken plate", price_ugx: 8500, quantity: 25 },
          { name: "Mineral water 500ml", price_ugx: 1500, quantity: 100 },
        ] : [
          { name: "Mathematical set", price_ugx: 18000, quantity: 25 },
          { name: "Exercise book (96pg)", price_ugx: 2500, quantity: 200 },
          { name: "School novel set", price_ugx: 35000, quantity: 12 },
        ];
        await supabase.from("stock_items").insert(items.map(i => ({ ...i, merchant_id: m.id })));
      }
    },
    onSuccess: () => { toast.success("Merchant ready"); onDone(); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="p-8 shadow-card">
      <div className="font-display text-2xl">Set up your merchant</div>
      <p className="mt-1 text-sm text-muted-foreground">A few details and you're ready to accept wallet payments.</p>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5"><Label>Business name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sunset Canteen" /></div>
        <div className="space-y-1.5">
          <Label>Category</Label>
          <Select value={category} onValueChange={(v) => setCategory(v as typeof category)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {["canteen","bookshop","uniform","bus","clinic","hostel","events","general"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label>School</Label>
          <Select value={schoolId} onValueChange={setSchoolId}>
            <SelectTrigger><SelectValue placeholder={schools[0]?.name ?? "Pick a school"} /></SelectTrigger>
            <SelectContent>{(schools as Array<{id:string;name:string}>).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        <Button onClick={() => submit.mutate(true)} disabled={submit.isPending}>Create with demo stock</Button>
        <Button variant="outline" onClick={() => submit.mutate(false)} disabled={submit.isPending}>Create empty</Button>
      </div>
    </Card>
  );
}

/* =============================================================
   CONSOLE SHELL — sidebar-driven mini operating system
============================================================= */

type SectionKey =
  | "executive" | "pos" | "catalog" | "promo"
  | "orders" | "bulk" | "deliveries"
  | "inv" | "kitchen" | "shifts"
  | "finance" | "settle" | "refunds"
  | "crm" | "compliance";

const NAV: { group: string; items: { key: SectionKey; label: string; icon: any; accent?: boolean }[] }[] = [
  { group: "Overview", items: [
    { key: "executive", label: "Executive", icon: Crown },
  ]},
  { group: "Commerce", items: [
    { key: "pos",     label: "POS",        icon: ShoppingCart },
    { key: "catalog", label: "Catalog",    icon: Package },
    { key: "promo",   label: "Promotions", icon: Megaphone },
  ]},
  { group: "Orders", items: [
    { key: "orders",     label: "Parent orders", icon: ClipboardList },
    { key: "bulk",       label: "School bulk",   icon: Building2 },
    { key: "deliveries", label: "Deliveries",    icon: Truck },
  ]},
  { group: "Inventory", items: [
    { key: "inv",     label: "Inventory AI", icon: Boxes },
    { key: "kitchen", label: "Kitchen",      icon: ChefHat },
    { key: "shifts",  label: "Shifts",       icon: Clock },
  ]},
  { group: "Finance", items: [
    { key: "finance", label: "Finance Center", icon: Banknote, accent: true },
    { key: "settle",  label: "Settlements",    icon: Banknote },
    { key: "refunds", label: "Refunds",        icon: ShieldAlert },
  ]},
  { group: "Relationships", items: [
    { key: "crm",        label: "CRM",        icon: Users },
    { key: "compliance", label: "Compliance", icon: ShieldCheck },
  ]},
];

const SECTION_META: Record<SectionKey, { title: string; subtitle: string }> = {
  executive:  { title: "Executive overview",  subtitle: "Revenue command center · live KPIs" },
  pos:        { title: "Point of Sale",       subtitle: "Charge wallets in seconds" },
  catalog:    { title: "Catalog",             subtitle: "Products, prices, photos" },
  promo:      { title: "Promotions",          subtitle: "Coupons & campaigns" },
  orders:     { title: "Parent orders",       subtitle: "Kanban from new to delivered" },
  bulk:       { title: "School bulk orders",  subtitle: "Quotation → PO → invoice" },
  deliveries: { title: "Deliveries",          subtitle: "Riders, routes, proof of delivery" },
  inv:        { title: "Inventory intelligence", subtitle: "Fast movers, dead stock, reorders" },
  kitchen:    { title: "Kitchen",             subtitle: "Prep boards, waste log" },
  shifts:     { title: "Shifts",              subtitle: "Cashier reconciliation" },
  finance:    { title: "Finance Center",      subtitle: "P&L · VAT · cash flow" },
  settle:     { title: "Settlements",         subtitle: "Payouts to your bank" },
  refunds:    { title: "Refunds & disputes",  subtitle: "Resolution queue" },
  crm:        { title: "Customer relationships", subtitle: "Top wallets and schools" },
  compliance: { title: "Compliance",          subtitle: "KYC, licences, URA TIN" },
};

function ConsoleShell({ merchant }: { merchant: Merchant }) {
  const qc = useQueryClient();
  const [active, setActive] = useState<SectionKey>("executive");
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState("");

  const { data: stock = [] } = useQuery({
    queryKey: ["stock", merchant.id],
    queryFn: async () => (await supabase.from("stock_items").select("*").eq("merchant_id", merchant.id).order("created_at", { ascending: false })).data as StockItem[] ?? [],
  });
  const { data: sales = [] } = useQuery({
    queryKey: ["sales", merchant.id],
    refetchInterval: 4000,
    queryFn: async () => (await supabase.from("pos_sales").select("*").eq("merchant_id", merchant.id).order("created_at", { ascending: false }).limit(200)).data ?? [],
  });
  const { data: txns = [] } = useQuery({
    queryKey: ["m-txns", merchant.id],
    refetchInterval: 4000,
    queryFn: async () => (await supabase.from("transactions").select("*").eq("merchant_id", merchant.id).order("created_at", { ascending: false }).limit(200)).data ?? [],
  });
  const { data: orders = [] } = useQuery({
    queryKey: ["m-orders", merchant.id],
    refetchInterval: 6000,
    queryFn: async () => (await supabase.from("merchant_orders").select("*").eq("merchant_id", merchant.id).order("created_at", { ascending: false })).data ?? [],
  });
  const { data: settlements = [] } = useQuery({
    queryKey: ["m-settle", merchant.id],
    queryFn: async () => (await supabase.from("settlements").select("*").eq("merchant_id", merchant.id).order("created_at", { ascending: false }).limit(30)).data ?? [],
  });
  const { data: promotions = [] } = useQuery({
    queryKey: ["m-promos", merchant.id],
    queryFn: async () => (await supabase.from("merchant_promotions").select("*").eq("merchant_id", merchant.id).order("created_at", { ascending: false })).data ?? [],
  });

  const now = Date.now();
  const today = sales.filter(s => now - new Date(s.created_at).getTime() < 86400000);
  const week  = sales.filter(s => now - new Date(s.created_at).getTime() < 7 * 86400000);
  const todayTotal = today.reduce((a, s) => a + (s.total_ugx ?? 0), 0);
  const fees = txns.reduce((a, t) => a + (t.fee_ugx ?? 0), 0);

  const topSellers = useMemo(() => {
    const map = new Map<string, { name: string; qty: number; revenue: number }>();
    for (const s of week) for (const it of (s.items as any[]) ?? []) {
      const cur = map.get(it.id) ?? { name: it.name, qty: 0, revenue: 0 };
      cur.qty += it.qty; cur.revenue += it.qty * it.price_ugx;
      map.set(it.id, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
  }, [week]);

  // Live search across nav
  const navFiltered = useMemo(() => {
    if (!query.trim()) return NAV;
    const q = query.toLowerCase();
    return NAV.map(g => ({ ...g, items: g.items.filter(i => i.label.toLowerCase().includes(q) || g.group.toLowerCase().includes(q)) })).filter(g => g.items.length > 0);
  }, [query]);

  const meta = SECTION_META[active];

  return (
    <div className="-mx-4 sm:-mx-6 -my-8">
      <div className="flex min-h-[calc(100vh-4rem)]">
        {/* Sidebar */}
        <aside className={cn(
          "shrink-0 border-r bg-card transition-all duration-200",
          collapsed ? "w-16" : "w-64"
        )}>
          <div className="sticky top-16 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="flex items-center justify-between gap-2 border-b p-3">
              {!collapsed && (
                <div className="flex items-center gap-2 min-w-0">
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg gradient-hero"><Store className="h-4 w-4 text-primary-foreground" /></div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{merchant.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{merchant.category}</div>
                  </div>
                </div>
              )}
              <button onClick={() => setCollapsed(c => !c)} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
                {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
              </button>
            </div>

            {!collapsed && (
              <div className="border-b p-3">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search console…" className="h-8 pl-7 text-xs" />
                </div>
              </div>
            )}

            <nav className="p-2">
              {navFiltered.map(g => (
                <div key={g.group} className="mb-3">
                  {!collapsed && <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{g.group}</div>}
                  <div className="space-y-0.5">
                    {g.items.map(it => {
                      const Icon = it.icon;
                      const isActive = active === it.key;
                      return (
                        <button
                          key={it.key}
                          onClick={() => setActive(it.key)}
                          title={collapsed ? it.label : undefined}
                          className={cn(
                            "group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition",
                            isActive
                              ? "bg-primary text-primary-foreground shadow-sm"
                              : "text-foreground/80 hover:bg-muted hover:text-foreground",
                            it.accent && !isActive && "text-primary"
                          )}
                        >
                          <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-primary-foreground" : "text-muted-foreground group-hover:text-foreground", it.accent && !isActive && "text-primary")} />
                          {!collapsed && <span className="truncate">{it.label}</span>}
                          {!collapsed && it.accent && !isActive && <Sparkles className="ml-auto h-3 w-3 text-primary/70" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0">
          {/* Top bar */}
          <div className="sticky top-16 z-30 glass border-b">
            <div className="flex flex-wrap items-center gap-3 px-6 py-3">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Merchant Console</div>
                <div className="font-display text-xl leading-tight truncate">{meta.title}</div>
              </div>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <div className="hidden md:flex items-center gap-1 rounded-lg border bg-card px-3 py-1.5 text-xs">
                  <span className="text-muted-foreground">Today</span>
                  <span className="font-mono font-semibold">{fmtUGX(todayTotal)}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">{today.length} sales</span>
                </div>
                <LoadDemoButton merchantId={merchant.id} onDone={() => qc.invalidateQueries()} />
                <Button size="sm" variant="outline" onClick={() => setActive("settle")}>
                  <Banknote className="mr-1.5 h-3.5 w-3.5" />Settle now
                </Button>
                <Button size="sm" onClick={() => setActive("pos")}>
                  <ShoppingCart className="mr-1.5 h-3.5 w-3.5" />Open POS
                </Button>
              </div>
            </div>
            <div className="px-6 pb-2 text-xs text-muted-foreground">{meta.subtitle}</div>
            <SettlementStrip settlements={settlements} onOpen={() => setActive("settle")} />

          </div>

          <div className="px-6 py-6">
            {active === "executive" && <ExecutivePanel merchantId={merchant.id} feeBps={merchant.fee_rate_bps} />}
            
            {active === "pos" && (
              <POSCheckout merchantId={merchant.id} stock={stock} feeBps={merchant.fee_rate_bps} onSale={() => { qc.invalidateQueries({ queryKey: ["sales", merchant.id] }); qc.invalidateQueries({ queryKey: ["stock", merchant.id] }); qc.invalidateQueries({ queryKey: ["m-txns", merchant.id] }); }} />
            )}
            {active === "catalog" && <>
              <StockManager merchantId={merchant.id} stock={stock} onChange={() => qc.invalidateQueries({ queryKey: ["stock", merchant.id] })} />
              {stock.some(s => s.quantity < 10) && (
                <Card className="mt-4 p-4 border-destructive/50 bg-destructive/5">
                  <div className="flex items-center gap-2 font-medium text-destructive"><AlertTriangle className="h-4 w-4" />Low stock alerts</div>
                  <div className="mt-2 text-sm">{stock.filter(s => s.quantity < 10).map(s => s.name).join(" · ")}</div>
                </Card>
              )}
              <SchoolMenuPanel schoolId={merchant.school_id} merchantId={merchant.id} />
            </>}
            {active === "promo" && <PromotionsPanel merchantId={merchant.id} promotions={promotions} onRefresh={() => qc.invalidateQueries({ queryKey: ["m-promos", merchant.id] })} />}
            {active === "orders" && <OrdersPanel merchantId={merchant.id} orders={orders} onRefresh={() => qc.invalidateQueries({ queryKey: ["m-orders", merchant.id] })} />}
            {active === "bulk" && <BulkOrdersPanel merchantId={merchant.id} />}
            {active === "deliveries" && <DeliveriesPanel merchantId={merchant.id} />}
            {active === "inv" && <InventoryIntelligencePanel merchantId={merchant.id} />}
            {active === "kitchen" && <KitchenPanel merchantId={merchant.id} />}
            {active === "shifts" && <ShiftsPanel merchantId={merchant.id} />}
            {active === "finance" && <FinancePanel merchantId={merchant.id} feeBps={merchant.fee_rate_bps} />}
            {active === "settle" && <MerchantSettlements merchantId={merchant.id} />}
            {active === "refunds" && <RefundsPanel merchantId={merchant.id} />}
            {active === "crm" && <CRMPanel merchantId={merchant.id} />}
            {active === "compliance" && <ComplianceDocsPanel merchantId={merchant.id} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function SettlementStrip({ settlements, onOpen }: { settlements: any[]; onOpen: () => void }) {
  const pending = settlements.filter((s: any) => s.status === "queued" || s.status === "processing");
  const lastPaid = settlements.find((s: any) => s.status === "paid");
  const pendingNet = pending.reduce((a: number, s: any) => a + (s.net_ugx ?? 0), 0);
  const pendingTx = pending.reduce((a: number, s: any) => a + (s.tx_count ?? 0), 0);
  const nextBatch = (() => {
    const d = new Date(); d.setHours(18, 0, 0, 0);
    if (Date.now() > d.getTime()) d.setDate(d.getDate() + 1);
    const ms = d.getTime() - Date.now();
    const h = Math.floor(ms / 3.6e6); const m = Math.floor((ms % 3.6e6) / 6e4);
    return `${h}h ${m}m`;
  })();
  return (
    <button onClick={onOpen} className="group block w-full border-t bg-gradient-to-r from-emerald-50/60 via-amber-50/40 to-transparent px-6 py-2 text-left transition hover:bg-muted/40">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px]">
        <div className="flex items-center gap-1.5">
          <span className="grid h-5 w-5 place-items-center rounded-full bg-amber-100 text-amber-700"><Wallet className="h-3 w-3" /></span>
          <span className="text-muted-foreground">Pending payout</span>
          <span className="font-mono font-semibold text-foreground">{fmtUGX(pendingNet)}</span>
          <span className="text-muted-foreground">· {pendingTx} tx</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">Next batch cut-off</span>
          <span className="font-mono font-semibold text-foreground">{nextBatch}</span>
          <Badge variant="outline" className="ml-1 h-4 border-emerald-200 bg-emerald-50 text-[9px] text-emerald-700">T+0</Badge>
        </div>
        {lastPaid && (
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-3 w-3 text-emerald-600" />
            <span className="text-muted-foreground">Last paid</span>
            <span className="font-mono font-semibold text-foreground">{fmtUGX(lastPaid.net_ugx)}</span>
            <span className="text-muted-foreground">· {lastPaid.reference ?? "—"}</span>
          </div>
        )}
        <span className="ml-auto text-[10px] text-primary opacity-0 transition group-hover:opacity-100">Open settlements cockpit →</span>
      </div>
    </button>
  );
}

function SettlementsList({ settlements }: { settlements: any[] }) {
  return (
    <Card>
      <div className="border-b p-4 font-display text-lg">Settlement history</div>
      <div className="divide-y">
        {settlements.map((s: any) => (
          <div key={s.id} className="flex items-center justify-between p-3 text-sm">
            <div>
              <div className="font-medium">{s.reference ?? s.id.slice(0, 8)}</div>
              <div className="text-xs text-muted-foreground">{shortDateTime(s.created_at)} · {s.tx_count} tx</div>
            </div>
            <div className="text-right">
              <div className="font-mono">{fmtUGX(s.net_ugx)}</div>
              <Badge variant="outline" className="text-[10px]">{s.status}</Badge>
            </div>
          </div>
        ))}
        {settlements.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No settlements yet. Daily batches settle at end-of-day.</div>}
      </div>
    </Card>
  );
}

function SchoolMenuPanel({ schoolId, merchantId }: { schoolId: string; merchantId: string }) {
  const { data: menus = [] } = useQuery({
    queryKey: ["school-menus", schoolId],
    queryFn: async () => (await (supabase.from as any)("school_menus").select("*").eq("school_id", schoolId).eq("for_date", new Date().toISOString().slice(0, 10))).data ?? [],
  });
  if (menus.length === 0) return null;
  return (
    <Card className="mt-4">
      <div className="border-b p-4 font-display text-lg flex items-center gap-2"><ChefHat className="h-4 w-4" />Today's school menu</div>
      <div className="grid gap-3 p-4 md:grid-cols-3">
        {menus.map((m: any) => (
          <div key={m.id} className="rounded-xl border bg-muted/30 p-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{m.meal}</div>
            <ul className="mt-2 space-y-1 text-sm">
              {(m.items as any[]).map((it, i) => (
                <li key={i} className="flex justify-between">
                  <span>{it.name}</span>
                  <span className="font-mono text-xs">{fmtUGX(it.price_ugx)}</span>
                </li>
              ))}
            </ul>
            {m.notes && <div className="mt-2 text-[11px] text-muted-foreground italic">{m.notes}</div>}
          </div>
        ))}
      </div>
    </Card>
  );
}


function KpiBox({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className="p-5 shadow-card">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-3xl">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </Card>
  );
}

function ComplianceRow({ label, status, detail }: { label: string; status: string; detail: string }) {
  return (
    <div className="flex items-center justify-between border-b pb-3 last:border-0">
      <div>
        <div className="font-medium text-sm">{label}</div>
        <div className="text-xs text-muted-foreground">{detail}</div>
      </div>
      <Badge variant={status === "approved" || status === "active" ? "secondary" : "destructive"} className="capitalize"><CheckCircle2 className="mr-1 h-3 w-3" />{status}</Badge>
    </div>
  );
}

function OrdersPanel({ merchantId, orders, onRefresh }: { merchantId: string; orders: any[]; onRefresh: () => void }) {
  const advance = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("merchant_orders").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Order updated"); onRefresh(); },
  });
  const cols: Record<string, any[]> = { pending: [], processing: [], ready: [], delivered: [] };
  for (const o of orders) (cols[o.status] ?? cols.pending).push(o);
  const next: Record<string, string> = { pending: "processing", processing: "ready", ready: "delivered" };
  return (
    <div className="grid gap-4 md:grid-cols-4">
      {(["pending", "processing", "ready", "delivered"] as const).map(col => (
        <Card key={col} className="overflow-hidden">
          <div className="border-b p-3 text-sm font-semibold capitalize">{col} <Badge variant="outline" className="ml-1 text-[10px]">{cols[col].length}</Badge></div>
          <div className="divide-y max-h-[400px] overflow-auto">
            {cols[col].map(o => (
              <div key={o.id} className="p-3 text-sm">
                <div className="font-medium">{fmtUGX(o.total_ugx)}</div>
                <div className="text-xs text-muted-foreground">{(o.items as any[])?.length ?? 0} items · {shortDateTime(o.created_at)}</div>
                {o.note && <div className="mt-1 text-xs italic">"{o.note}"</div>}
                {next[col] && <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => advance.mutate({ id: o.id, status: next[col] })}>→ {next[col]}</Button>}
              </div>
            ))}
            {cols[col].length === 0 && <div className="p-4 text-center text-xs text-muted-foreground">Empty</div>}
          </div>
        </Card>
      ))}
    </div>
  );
}

function PromotionsPanel({ merchantId, promotions, onRefresh }: { merchantId: string; promotions: any[]; onRefresh: () => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("Back-to-school 10% off");
  const [discount, setDiscount] = useState("10");
  const create = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("merchant_promotions").insert({ merchant_id: merchantId, title, discount_pct: Number(discount) });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Promotion live"); setOpen(false); onRefresh(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const toggle = useMutation({
    mutationFn: async (p: any) => { await supabase.from("merchant_promotions").update({ active: !p.active }).eq("id", p.id); },
    onSuccess: onRefresh,
  });
  return (
    <Card>
      <div className="flex items-center justify-between border-b p-4">
        <div className="font-display text-lg">Promotions & campaigns</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm"><Plus className="mr-1 h-3 w-3" />New</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create promotion</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Discount %</Label><Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} /></div>
              <DialogFooter><Button onClick={() => create.mutate()} disabled={!title}>Publish</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="divide-y">
        {promotions.map(p => (
          <div key={p.id} className="flex items-center justify-between p-3 text-sm">
            <div>
              <div className="font-medium">{p.title}</div>
              <div className="text-xs text-muted-foreground">{p.discount_pct}% off · since {shortDateTime(p.created_at)}</div>
            </div>
            <Button size="sm" variant={p.active ? "secondary" : "outline"} onClick={() => toggle.mutate(p)}>{p.active ? "Active" : "Paused"}</Button>
          </div>
        ))}
        {promotions.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No promotions yet. Drive footfall with back-to-school bundles or student vouchers.</div>}
      </div>
    </Card>
  );
}
type CartItem = { id: string; name: string; price_ugx: number; qty: number; max: number };

/** One-tap demo data loader — calls the seed_demo_for_merchant RPC. */
function LoadDemoButton({ merchantId, onDone }: { merchantId: string; onDone: () => void }) {
  const seed = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase as any).rpc("seed_demo_for_merchant", { _merchant_id: merchantId });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Demo data loaded · stock, sales, orders, cards"); onDone(); },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Button size="sm" variant="outline" onClick={() => seed.mutate()} disabled={seed.isPending}>
      <Database className="mr-1.5 h-3.5 w-3.5" />
      {seed.isPending ? "Loading…" : "Load demo data"}
    </Button>
  );
}

/* POSCheckout + StudentCardSnapshot were extracted to src/features/merchant-pos.tsx */


/* ------------ Stock manager ------------ */
function StockManager({ merchantId, stock, onChange }: { merchantId: string; stock: StockItem[]; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("2000");
  const [qty, setQty] = useState("50");

  const add = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("stock_items").insert({
        merchant_id: merchantId, name, price_ugx: Number(price), quantity: Number(qty),
      });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Item added"); onChange(); setOpen(false); setName(""); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <div className="flex items-center justify-between border-b p-4">
        <div className="font-display text-lg">Stock</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" variant="outline"><Plus className="mr-1 h-3 w-3" />Add</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add stock item</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Chapati" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Price (UGX)</Label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Quantity</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} /></div>
              </div>
              <DialogFooter><Button onClick={() => add.mutate()} disabled={!name || add.isPending}>Add item</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="divide-y">
        {stock.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground"><Package className="mx-auto mb-2 h-6 w-6 opacity-50" />No stock yet. Add your first item.</div>}
        {stock.map((s) => (
          <div key={s.id} className="flex items-center justify-between p-3 text-sm">
            <div>
              <div className="font-medium">{s.name}</div>
              <div className="text-xs text-muted-foreground">{fmtUGX(s.price_ugx)}</div>
            </div>
            <Badge variant={s.quantity < 10 ? "destructive" : "secondary"}>{s.quantity} left</Badge>
          </div>
        ))}
      </div>
    </Card>
  );
}
